<script setup lang="ts">
import { bemBuilder, toMoney } from "@chatfood/core-utils";
import { ref, computed } from "vue";
import {
  AtomIcon,
  AtomButton,
  SatProductInfo,
  SatNutritionInfo,
  OrgModifierGroup,
  useOrgModifierGroup,
  type IModifierGroupEmitChangePayload,
  type IModifierGroupEmitValidatePayload,
  type IModifierGroupEmitCustomizeModifierPayload,
} from "@chatfood/design-system";
import { ItemDetails } from "../item-details";
import type { IItemHandlerProps, IItemHandlerSelected } from "./props";
import type { IItemHandlerEmits } from "./emits";
import { useLocale } from "~/composables/use-locale";
import type { IDomainModifierList } from "~/domain/modifier-list";

const css = bemBuilder("item-handler");

const props = defineProps<IItemHandlerProps>();
const emits = defineEmits<IItemHandlerEmits>();

const { t } = useLocale();

const modifierLists = computed(() => {
  return props.item.modifierLists.map((modifierList) => ({
    ...modifierList,
    items: modifierList.modifiers.map((item) => ({
      id: item.id,
      name: item.name,
      price: item.price,
      quantity: getItemQuantity(modifierList.id, item.id),
      cta: ctaLabel(modifierList.id, item.id, item?.modifierLists),
    })),
  }));
});

function getItemQuantity(modifierListId: string, modifierId: string) {
  return findItemSelected(modifierListId, modifierId)?.quantity ?? 0;
}

function ctaLabel(
  modifierListId: string,
  itemId: string,
  modifierLists: IDomainModifierList[] = [],
) {
  const itemSelected = findItemSelected(modifierListId, itemId);
  const quantity = getItemQuantity(modifierListId, itemId);

  if (quantity > 0 && modifierLists.length) {
    const minSelections = modifierLists.reduce(
      (acc, cur) =>
        // add +1 for each modifier list required not selected
        cur.minSelection > sumItemsSelected(cur.id) ? acc + 1 : acc,
      0,
    );

    function sumItemsSelected(modifierListId: string) {
      if (!itemSelected?.items?.length) return 0;

      return itemSelected.items.reduce(
        (acc, item) =>
          item.modifierListId === modifierListId ? acc + item.quantity : acc,
        0,
      );
    }

    if (minSelections > 0) {
      return {
        label: t("components.item_handler.min_selections_required", {
          count: minSelections,
        }),
        hasError: true,
      };
    }

    return {
      label: t("components.item_handler.customizable"),
      hasError: false,
    };
  }
}

function generateSubtitle(min: number, max: number) {
  if (min > 0) {
    if (max === min && max > 1) {
      return t("components.item_handler.choose_at_least_x", { min });
    }

    if (max > 1) {
      return t("components.item_handler.choose_at_least_x_up_to_x", {
        max,
        min,
      });
    }

    if (max === 1) {
      return t("components.item_handler.choose_1");
    }
  }

  if (max > 0) {
    return t("components.item_handler.choose_up_to_x", { max });
  }

  return "";
}

function findModifierList(modifierListId: string) {
  return modifierLists.value.find(
    (modifierList) => modifierList.id === modifierListId,
  );
}

function findModifierListSelected(modifierListId: string) {
  return props.modifierListSelected.find(
    (modifierList) => modifierList.id === modifierListId,
  );
}

function findItemSelected(modifierListId: string, itemId: string) {
  return findModifierListSelected(modifierListId)?.items.find(
    (item) => item.id === itemId,
  );
}

const { updateItems } = useOrgModifierGroup();

async function onChange(payload: IModifierGroupEmitChangePayload) {
  const modifierList = findModifierList(payload.categoryId);

  const createModifierList = await updateItems({
    itemId: payload.itemId,
    decrease: payload.decrease,
    type: payload.type,
    items: modifierList?.items ?? [],
  });

  const filterItems = createModifierList
    .filter((item) => item.quantity > 0)
    .map((item) => ({
      id: item.id,
      name: item.name,
      quantity: item.quantity,
      price: item.price,
      modifierListId: payload.categoryId,
    }));

  handleSelectedModifierList(
    payload.categoryId,
    payload.itemId,
    payload.type,
    filterItems,
  );
}

function handleSelectedModifierList(
  modifierListId: string,
  itemId: string,
  type: IDomainModifierList["selectType"],
  modifierList: IItemHandlerSelected[] = [],
) {
  let newModifierList = modifierList;

  const findModifierList = findModifierListSelected(modifierListId);

  const preserveSelecteds = findModifierList?.items.filter(
    (item) => item?.items?.length,
  );

  if (type !== "radio" && preserveSelecteds?.length) {
    newModifierList = modifierList.map((modifier) => {
      const findSelectedModifier = preserveSelecteds.find(
        (selected) =>
          selected.modifierListId === modifier.modifierListId &&
          selected.id === modifier.id,
      );

      return {
        ...modifier,
        items: findSelectedModifier?.items ?? [],
      };
    });
  }

  const newModifierListSelected = props.modifierListSelected.filter(
    (modifierList) => modifierList.id !== modifierListId,
  );

  if (newModifierList.length) {
    newModifierListSelected.push({
      id: modifierListId,
      items: newModifierList,
    });
  }

  emits("change", {
    modifierListSelected: newModifierListSelected,
    modifierListId,
    itemId,
  });
}

function onValidate(payload: IModifierGroupEmitValidatePayload) {
  emits("validate", payload);
}

function onCustomizeModifier(
  payload: IModifierGroupEmitCustomizeModifierPayload,
) {
  emits("customizeModifier", payload);
}

const complementaryPrice = computed(() => {
  return props.item.bottleDepositPrice.value > 0
    ? t("components.item_handler.bottle_deposit", {
        price: toMoney(
          props.item.bottleDepositPrice.value,
          props.item.bottleDepositPrice.currency,
        ),
      })
    : "";
});

const isInfoOpened = ref(false);
</script>

<template>
  <div :class="css()">
    <div v-if="!isInfoOpened" data-test="main-content">
      <div :class="css('content')">
        <SatProductInfo
          :name="props.item.name"
          :description="props.item.description"
          :price="props.item.price"
          :original-price="props.item.originalPrice"
          :complementary-price="complementaryPrice"
        />
        <SatNutritionInfo
          v-if="props.item.consumables.length"
          :tags="props.item.consumables"
        />
        <div>
          <AtomButton
            wide
            size="small"
            variant="secondary"
            :class="css('button')"
            data-test="btn-specifications"
            @click.prevent="isInfoOpened = true"
          >
            {{ t("components.item_handler.info_cta") }}
            <AtomIcon name="caret-right-alt" size="small" ignore-color />
          </AtomButton>
        </div>
      </div>
      <div v-if="modifierLists.length" :class="css('modifier-list')">
        <div
          v-for="modifierList in modifierLists"
          :id="modifierList.id"
          :key="modifierList.id"
          :class="css('modifier-list-item')"
        >
          <OrgModifierGroup
            validate-on-mount
            :class="css('modifier-list-box')"
            :title="modifierList.name"
            :category-id="modifierList.id"
            :view-only="props.item.inStock === false"
            :subtitle="
              generateSubtitle(
                modifierList.minSelection,
                modifierList.maxSelection,
              )
            "
            :items="modifierList.items"
            :type="modifierList.selectType"
            :max-selection="modifierList.maxSelection"
            :min-selection="modifierList.minSelection"
            @change="onChange"
            @validate="onValidate"
            @customize-modifier="onCustomizeModifier"
          />
        </div>
      </div>
    </div>
    <div v-else data-test="specifications-content">
      <AtomButton
        size="small"
        variant="secondary"
        :class="css('button')"
        data-test="close-specifications"
        @click.prevent="isInfoOpened = false"
      >
        <AtomIcon name="back" ignore-color />
        {{ t("components.item_handler.back_cta") }}
      </AtomButton>
      <ItemDetails :item="props.item" :class="css('item-details')" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.item-handler {
  position: relative;

  &__content {
    position: relative;
    display: flex;
    flex-direction: column;

    > div {
      padding-block: var(--size-md);
      border-bottom: solid 1px var(--color-base-95);

      &:first-child {
        padding-top: 0;
      }
    }
  }

  &__button {
    display: flex;
    align-items: center;
    gap: var(--size-md);
    justify-content: space-between;
  }

  &__item-details {
    margin-top: var(--size-md);
  }

  &__modifier-list {
    display: flex;
    flex-direction: column;

    &-item {
      padding-top: var(--size-md);
    }

    &-box {
      border-radius: var(--radii-card-sm);
      border: 1px solid var(--color-base-95);
      padding: 0 var(--spacing-md) var(--spacing-md);
    }
  }
}
</style>
